import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  Search, 
  ClipboardList, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Eye, 
  Download, 
  Loader2,
  RotateCcw,
  Building2,
  User,
  MapPin,
  Trash2,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'En attente', class: 'bg-amber-100 text-amber-700', icon: Clock },
  validated: { label: 'Validee', class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Rejetee', class: 'bg-red-100 text-red-700', icon: XCircle },
};

export const AdminFDC = () => {
  const [fdcs, setFdcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFdc, setSelectedFdc] = useState(null);
  const [fdcDocuments, setFdcDocuments] = useState([]);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState(null);

  useEffect(() => {
    fetchFdcs();
  }, []);

  const fetchFdcs = async () => {
    try {
      const response = await fetch(`${API}/admin/fdc`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFdcs(data);
      }
    } catch (error) {
      console.error('Failed to fetch FDCs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFdcDocuments = async (fdcId) => {
    try {
      const response = await fetch(`${API}/fdc/${fdcId}/documents`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        console.log(`[AdminFDC] /fdc/${fdcId}/documents returned`, data.length, 'docs');
        setFdcDocuments(data);
      } else {
        const err = await response.text();
        console.error(`[AdminFDC] /fdc/${fdcId}/documents failed`, response.status, err);
        toast.error(`Echec de chargement des documents (${response.status})`);
        setFdcDocuments([]);
      }
    } catch (error) {
      console.error('Failed to fetch FDC documents:', error);
      toast.error('Erreur de connexion au serveur');
      setFdcDocuments([]);
    }
  };

  const openDetail = async (fdc) => {
    setSelectedFdc(fdc);
    // Use inline additional_documents from /admin/fdc + contract file id
    // Always re-fetch via /fdc/{id}/documents to get the most up-to-date list
    setFdcDocuments([]);
    await fetchFdcDocuments(fdc.fdc_id);
    // Fallback: if API call returned 0 but FDC has docs metadata inline, use that
    setFdcDocuments((prev) => {
      if (prev && prev.length > 0) return prev;
      const inline = [];
      if (fdc.contract_file_id) {
        inline.push({
          file_id: fdc.contract_file_id,
          doc_type: 'contract',
          file_name: fdc.contract_filename || 'Contrat',
        });
      }
      if (Array.isArray(fdc.additional_documents)) {
        inline.push(...fdc.additional_documents);
      }
      return inline;
    });
    setIsDetailOpen(true);
  };

  const downloadFdcFile = async (fileId, filename, docType) => {
    setDownloadingFile(fileId);
    try {
      const response = await fetch(
        `${API}/fdc/${selectedFdc.fdc_id}/documents/${fileId}/download`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || `${docType}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Document telecharge');
      } else {
        toast.error('Erreur lors du telechargement');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur lors du telechargement');
    } finally {
      setDownloadingFile(null);
    }
  };

  const downloadContract = async (fdcId) => {
    setDownloading(true);
    try {
      const response = await fetch(`${API}/admin/fdc/${fdcId}/download-contract`, {
        credentials: 'include',
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `contrat_${fdcId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Contrat telecharge');
      } else {
        toast.error('Erreur lors du telechargement');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur lors du telechargement');
    } finally {
      setDownloading(false);
    }
  };

  const validateFdc = async (fdcId) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/fdc/${fdcId}/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      
      if (response.ok) {
        toast.success('Declaration validee - compte commercial cree');
        setFdcs(prev => prev.map(f => f.fdc_id === fdcId ? { ...f, status: 'validated' } : f));
        if (selectedFdc?.fdc_id === fdcId) {
          setSelectedFdc(prev => ({ ...prev, status: 'validated' }));
        }
        setIsDetailOpen(false);
        fetchFdcs(); // Refresh list
      } else if (response.status === 400) {
        toast.error('Erreur: cet email est peut-etre deja utilise');
      } else if (response.status === 404) {
        toast.error('Declaration non trouvee');
      } else {
        toast.error('Erreur lors de la validation');
      }
    } catch (error) {
      console.error('Validation error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectDialog = (fdc) => {
    setSelectedFdc(fdc);
    setIsRejectOpen(true);
  };

  const handleReject = async () => {
    if (!rejectComment.trim()) {
      toast.error('Veuillez indiquer le motif du rejet');
      return;
    }
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/fdc/${selectedFdc.fdc_id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ comment: rejectComment }),
      });
      
      if (response.ok) {
        toast.success('Declaration rejetee');
        setFdcs(prev => prev.map(f => 
          f.fdc_id === selectedFdc.fdc_id 
            ? { ...f, status: 'rejected', rejection_comment: rejectComment } 
            : f
        ));
        setIsRejectOpen(false);
        setIsDetailOpen(false);
        setRejectComment('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors du rejet');
      }
    } catch (error) {
      console.error('Reject error:', error);
      toast.error('Erreur lors du rejet');
    } finally {
      setActionLoading(false);
    }
  };

  const resetToPending = async (fdcId) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/fdc/${fdcId}/reset`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        toast.success('Declaration remise en attente');
        setFdcs(prev => prev.map(f => f.fdc_id === fdcId ? { ...f, status: 'pending' } : f));
      }
    } catch (error) {
      console.error('Reset error:', error);
      toast.error('Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteDialog = (fdc) => {
    setSelectedFdc(fdc);
    setIsDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedFdc) return;
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/fdc/${selectedFdc.fdc_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (response.ok) {
        toast.success('Declaration supprimee definitivement');
        setFdcs(prev => prev.filter(f => f.fdc_id !== selectedFdc.fdc_id));
        setIsDeleteOpen(false);
        setIsDetailOpen(false);
      } else {
        toast.error('Impossible de supprimer une declaration validee');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredFdcs = fdcs.filter(fdc => {
    const fullName = `${fdc.commercial_firstname} ${fdc.commercial_lastname}`.toLowerCase();
    const companyName = fdc.company_name?.toLowerCase() || '';
    return fullName.includes(searchQuery.toLowerCase()) || 
           companyName.includes(searchQuery.toLowerCase()) ||
           fdc.commercial_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           fdc.rh_name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const pendingCount = fdcs.filter(f => f.status === 'pending').length;
  const validatedCount = fdcs.filter(f => f.status === 'validated').length;
  const rejectedCount = fdcs.filter(f => f.status === 'rejected').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-[Manrope]" data-testid="admin-fdc-title">
          Declarations RH (FDC)
        </h1>
        <p className="text-slate-500 mt-1">Validez les declarations de contrat des RH</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-indigo-100">
              <ClipboardList className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total</p>
              <p className="text-xl font-bold text-slate-900">{fdcs.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-100">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">En attente</p>
              <p className="text-xl font-bold text-amber-600">{pendingCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-100">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Validees</p>
              <p className="text-xl font-bold text-emerald-600">{validatedCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-red-100">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Rejetees</p>
              <p className="text-xl font-bold text-red-600">{rejectedCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Rechercher par nom, societe, email ou RH..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-fdc-input"
        />
      </div>

      {/* FDCs Table */}
      <Card className="border-0 shadow-sm overflow-hidden" data-testid="fdc-table-card">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-900">
                <TableHead className="font-semibold">Societe</TableHead>
                <TableHead className="font-semibold">Commercial</TableHead>
                <TableHead className="font-semibold">Email</TableHead>
                <TableHead className="font-semibold">RH declarant</TableHead>
                <TableHead className="font-semibold">Type contrat</TableHead>
                <TableHead className="font-semibold">PJ</TableHead>
                <TableHead className="font-semibold">Date</TableHead>
                <TableHead className="font-semibold">Statut</TableHead>
                <TableHead className="font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : filteredFdcs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                    Aucune declaration
                  </TableCell>
                </TableRow>
              ) : (
                filteredFdcs.map((fdc, index) => {
                  const StatusIcon = statusConfig[fdc.status]?.icon || Clock;
                  return (
                    <TableRow 
                      key={fdc.fdc_id}
                      className={`table-row-hover animate-fade-in ${fdc.status === 'rejected' ? 'bg-red-50' : ''}`}
                      style={{ animationDelay: `${index * 0.02}s` }}
                    >
                      <TableCell className="font-medium">
                        {fdc.company_name || '-'}
                      </TableCell>
                      <TableCell>
                        {fdc.commercial_firstname} {fdc.commercial_lastname}
                      </TableCell>
                      <TableCell>{fdc.commercial_email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                          {fdc.rh_name || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell>{fdc.contract_type}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-indigo-50 text-indigo-700" data-testid={`fdc-doc-count-${fdc.fdc_id}`}>
                          <FileText className="w-3 h-3 mr-1" />
                          {fdc.documents_count ?? ((fdc.contract_file_id ? 1 : 0) + (fdc.additional_documents?.length || 0))}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {new Date(fdc.created_at).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusConfig[fdc.status]?.class} border`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig[fdc.status]?.label}
                        </Badge>
                        {fdc.rejection_comment && (
                          <p className="text-xs text-red-600 mt-1 max-w-[150px] truncate" title={fdc.rejection_comment}>
                            {fdc.rejection_comment}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDetail(fdc)}
                            className="text-indigo-600"
                            data-testid={`view-fdc-${fdc.fdc_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {fdc.status === 'pending' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => validateFdc(fdc.fdc_id)}
                                className="text-emerald-600 hover:bg-emerald-50"
                                disabled={actionLoading}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openRejectDialog(fdc)}
                                className="text-red-600 hover:bg-red-50"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {fdc.status === 'rejected' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => resetToPending(fdc.fdc_id)}
                                className="text-amber-600"
                                disabled={actionLoading}
                              >
                                <RotateCcw className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDeleteDialog(fdc)}
                                className="text-red-600 hover:bg-red-50"
                                disabled={actionLoading}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {fdc.status === 'pending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(fdc)}
                              className="text-red-600 hover:bg-red-50"
                              disabled={actionLoading}
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Details de la declaration</DialogTitle>
            <DialogDescription>
              Declaration par {selectedFdc?.rh_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Company Info */}
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2 text-indigo-600">
                <Building2 className="w-4 h-4" />
                Informations de la societe
              </h3>
              <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Nom de la societe</p>
                  <p className="font-medium">{selectedFdc?.company_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Forme sociale</p>
                  <p className="font-medium">{selectedFdc?.company_legal_form || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-slate-500">Adresse complete</p>
                  <p className="font-medium">{selectedFdc?.company_address || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">SIREN</p>
                  <p className="font-medium">{selectedFdc?.company_siren || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Numero RSAC</p>
                  <p className="font-medium">{selectedFdc?.company_rsac_number || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Ville RSAC</p>
                  <p className="font-medium">{selectedFdc?.company_rsac_city || '-'}</p>
                </div>
              </div>
            </div>

            {/* Person Info */}
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2 text-indigo-600">
                <User className="w-4 h-4" />
                Representant legal
              </h3>
              <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Nom Prenom</p>
                  <p className="font-medium">{selectedFdc?.person_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Fonction</p>
                  <p className="font-medium">{selectedFdc?.person_function || '-'}</p>
                </div>
              </div>
            </div>

            {/* Commercial Info */}
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2 text-indigo-600">
                <MapPin className="w-4 h-4" />
                Informations du commercial
              </h3>
              <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Prenom</p>
                  <p className="font-medium">{selectedFdc?.commercial_firstname}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Nom</p>
                  <p className="font-medium">{selectedFdc?.commercial_lastname}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium">{selectedFdc?.commercial_email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Telephone</p>
                  <p className="font-medium">{selectedFdc?.commercial_phone || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Type de contrat</p>
                  <p className="font-medium">{selectedFdc?.contract_type}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Date signature</p>
                  <p className="font-medium">
                    {selectedFdc?.contract_date ? new Date(selectedFdc.contract_date).toLocaleDateString('fr-FR') : '-'}
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold flex items-center gap-2 text-indigo-600 mb-3">
                <FileText className="w-4 h-4" />
                Documents joints ({fdcDocuments.length})
              </h3>
              {fdcDocuments.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Aucun document</p>
              ) : (
                <div className="space-y-2">
                  {fdcDocuments.map((doc) => {
                    const docLabels = {
                      contract: 'Contrat signe',
                      dsh_clf: 'DSH - CLF signe',
                      autres_avenants: 'Autres & Avenants',
                    };
                    const fileId = doc.file_id;
                    const fileName = doc.file_name || `${doc.doc_type}.pdf`;
                    return (
                      <div key={fileId} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-indigo-500" />
                          <div>
                            <p className="font-medium text-sm">{docLabels[doc.doc_type] || doc.doc_type}</p>
                            <p className="text-xs text-slate-500">{fileName}</p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadFdcFile(fileId, fileName, doc.doc_type)}
                          disabled={downloadingFile === fileId}
                          data-testid={`download-fdc-doc-${doc.doc_type}`}
                        >
                          {downloadingFile === fileId ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Download className="w-4 h-4 mr-1" />
                              Telecharger
                            </>
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {selectedFdc?.rejection_comment && (
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm font-medium text-red-800">Motif du rejet:</p>
                <p className="text-sm text-red-700">{selectedFdc.rejection_comment}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            {selectedFdc?.status === 'pending' && (
              <>
                <Button
                  variant="outline"
                  onClick={() => openRejectDialog(selectedFdc)}
                  className="text-red-600"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Rejeter
                </Button>
                <Button
                  onClick={() => validateFdc(selectedFdc?.fdc_id)}
                  disabled={actionLoading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {actionLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Valider et creer le compte
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={isRejectOpen} onOpenChange={setIsRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeter la declaration</DialogTitle>
            <DialogDescription>
              Indiquez le motif du rejet pour que le RH puisse corriger
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              placeholder="Motif du rejet (ex: document illisible, informations manquantes...)"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRejectOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleReject}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
              Rejeter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer definitivement</DialogTitle>
            <DialogDescription>
              Etes-vous sur de vouloir supprimer cette declaration ? Cette action est irreversible.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 bg-red-50 rounded-lg p-4 border border-red-200">
            <p className="font-medium text-red-800">Declaration concernee :</p>
            <p className="text-red-700">{selectedFdc?.commercial_firstname} {selectedFdc?.commercial_lastname}</p>
            <p className="text-sm text-red-600">{selectedFdc?.company_name} - {selectedFdc?.commercial_email}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleDelete}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Supprimer definitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
